import { EyeIcon, EyeSlashIcon, MagnifyingGlassIcon, XIcon } from '@phosphor-icons/react'
import { type InputHTMLAttributes, useEffect, useRef, useState } from 'react'
import z from 'zod'
import { InputContainer, type InputContainerProps } from './input-container.tsx'
import * as styles from './text-input.css.ts'

export const TextInputSizeSchema = z.enum(['small', 'medium'])
export type TextInputSize = z.infer<typeof TextInputSizeSchema>

type InputType = Exclude<InputHTMLAttributes<HTMLInputElement>['type'], undefined>
export type TextInputProps = InputContainerProps & {
  /** Current value of the input */
  value?: string
  /** Callback fired when the input value changes */
  onChange?: (value: string) => unknown
  /** Aria-label attribute */
  ariaLabel?: string
  /** Size of the input */
  size?: TextInputSize
  /** Delay in milliseconds before onChange is fired after user stops typing */
  debounce?: number
  type?: Extract<InputType, 'text' | 'password' | 'search' | 'number'>
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'onChange' | 'value' | 'type'>

export function TextInput({
  value,
  onChange,
  placeholder = '',
  label,
  id,
  name,
  ariaLabel,
  isRequired,
  explicitLabelCondition,
  helperText,
  errorText,
  isError,
  type = 'text',
  className: externalClassName,
  size = 'medium',
  debounce = 0,
  ...props
}: TextInputProps) {
  const [showPassword, setShowPassword] = useState(false)
  const isPassword = type === 'password'
  const isSearch = type === 'search'

  return (
    <InputContainer
      externalClassName={externalClassName}
      label={label}
      id={id}
      isRequired={isRequired}
      explicitLabelCondition={explicitLabelCondition}
      helperText={helperText}
      errorText={errorText}
      isError={isError}
    >
      <DebouncedInput
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        ariaLabel={ariaLabel}
        id={id}
        name={name}
        type={isPassword ? (showPassword ? 'text' : 'password') : type}
        isPassword={isPassword}
        showPassword={showPassword}
        onTogglePassword={() => setShowPassword(!showPassword)}
        isSearch={isSearch}
        isError={isError}
        size={size}
        debounce={debounce}
        {...props}
      />
    </InputContainer>
  )
}

type DebouncedInputProps = TextInputProps & {
  /** Whether this is a password input with show/hide functionality */
  isPassword?: boolean
  /** Whether password is currently shown as plain text */
  showPassword?: boolean
  /** Callback fired when show/hide password button is clicked */
  onTogglePassword?: () => unknown
  /** Whether this is a search input with magnifying glass icon and clear button */
  isSearch?: boolean
  /** Whether the input is disabled */
  disabled?: boolean
}

function DebouncedInput({
  value: initialValue = '',
  onChange = () => {},
  debounce,
  placeholder,
  id,
  name,
  ariaLabel,
  isPassword,
  showPassword,
  onTogglePassword,
  isSearch,
  isError,
  disabled,
  size = 'medium',
  ...props
}: DebouncedInputProps) {
  const [value, setValue] = useState<string>(initialValue)

  // Ref captures latest onChange so the effect doesn't need it as a dep — inline arrows change identity every render.
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  useEffect(() => {
    if (debounce === 0) {
      onChangeRef.current(value)
      return
    }

    const timeout = setTimeout(() => {
      onChangeRef.current(value)
    }, debounce)

    return () => clearTimeout(timeout)
  }, [value, debounce])

  const handleClear = () => {
    setValue('')
    onChange('')
  }

  return (
    <div className={styles.inputContainer({ size })} data-disabled={disabled} data-error={isError || undefined}>
      {isSearch && <MagnifyingGlassIcon className={styles.searchIcon({ size })} weight="light" />}
      <input
        className={styles.textInput({ hasLeftIcon: isSearch, size })}
        value={value}
        placeholder={placeholder}
        onChange={e => setValue(e.target.value)}
        id={id}
        name={name}
        disabled={disabled}
        aria-label={ariaLabel}
        {...props}
      />
      {isPassword && (
        <button
          type="button"
          onClick={onTogglePassword}
          className={styles.eyeButton}
          aria-label={showPassword ? 'Hide password' : 'Show password'}
          disabled={disabled}
        >
          {showPassword ? <EyeIcon size={12} weight="fill" /> : <EyeSlashIcon size={12} weight="fill" />}
        </button>
      )}
      {isSearch && value && !disabled && (
        <button type="button" onClick={handleClear} className={styles.clearButton} aria-label="Clear search">
          <XIcon size={12} weight="light" />
        </button>
      )}
    </div>
  )
}
